/**
 * Sandbox tokens for a web page - a full-screen `url` item or a `web` region
 * on a template slide.
 *
 * `allow-same-origin` is what makes a real page work at all - without it the
 * document has an opaque origin, its storage throws, and a call to its own API
 * turns into a CORS request the server was never built to answer (see the long
 * note at the render site). It is granted to a cross-origin page, where it
 * restores only that page's own origin.
 *
 * It is WITHHELD when the page is on this panel's own origin, and that is the
 * one case the famous `allow-scripts` + `allow-same-origin` warning is actually
 * about: a same-origin frame can reach `parent.document`, remove the `sandbox`
 * attribute from its own element and reload unsandboxed. Nobody has a reason to
 * schedule Foyer inside Foyer, so the narrow case pays rather than every page.
 *
 * `allow-top-navigation` is absent from both branches on purpose. A lobby
 * screen has no keyboard and no one watching it; a page that can navigate the
 * panel off Foyer is unrecoverable without a site visit.
 *
 * An unparseable URL falls back to the strict set: it will not render anything
 * useful either way, and guessing generously about a value we could not read is
 * the wrong direction to be wrong in.
 */
export function iframeSandbox(url: string): string {
  const base =
    "allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox";
  try {
    const target = new URL(url, window.location.href);
    if (target.origin === window.location.origin) return base;
    return `${base} allow-same-origin`;
  } catch {
    return base;
  }
}
